import { v4 as uuidv4 } from 'uuid';

/**
 * Extracts unique room names from a list of schedule items.
 * @param {Array} items - Array of schedule item objects.
 * @returns {Array} - Sorted array of unique room names.
 */
export const getUniqueRooms = (items: any[]): string[] => {
    const rooms = new Set<string>();
    // Ensure items is an array before iterating
    if (!Array.isArray(items)) {
        console.error("getUniqueRooms received non-array:", items);
        return [];
    }
    items.forEach(item => {
        // Ensure item is valid and has a room property
        if (item && item.room) {
            rooms.add(item.room);
        }
    });
    // Sort rooms for consistent order
    return Array.from(rooms).sort((a, b) => {
        // Basic alphabetical sort, can be enhanced if needed
        return a.localeCompare(b);
    });
};

/**
 * Parses time information from a ScheduleEntry object.
 * @param {ScheduleEntry} scheduleEntry - The schedule entry object.
 * @returns {{startMinutes: number|null, endMinutes: number|null, durationMinutes: number|null}} - Object containing start, end, and duration in minutes from midnight, or null if invalid.
 */
export const parseTime = (timeString: string): { startMinutes: number | null; endMinutes: number | null; durationMinutes: number | null } => {
    if (!timeString || typeof timeString !== 'string') {
        throw new Error('timeString must be a string and exist.');
    }

    const parts = timeString.split(' - ');
    const timeRegex = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/;

    let startMatch: RegExpMatchArray | null = null;
    let endMatch: RegExpMatchArray | null = null;
    let startMinutes: number | null = null;
    let endMinutes: number | null = null;
    let durationMinutes: number | null = null;

    if (parts.length >= 1 && parts[0]) {
        startMatch = parts[0].match(timeRegex);
        if (startMatch && startMatch[1] && startMatch[2]) {
            const hours = parseInt(startMatch[1], 10);
            const minutes = parseInt(startMatch[2], 10);
            // Ignore seconds since we're working with minute precision
            if (hours >= 0 && hours < 24 && minutes >= 0 && minutes < 60) {
                startMinutes = hours * 60 + minutes;
            }
        }
    }

    if (parts.length === 2 && parts[1]) {
        endMatch = parts[1].match(timeRegex);
         if (endMatch && endMatch[1] && endMatch[2]) {
            const hours = parseInt(endMatch[1], 10);
            const minutes = parseInt(endMatch[2], 10);
             if (hours >= 0 && hours < 24 && minutes >= 0 && minutes < 60) {
                endMinutes = hours * 60 + minutes;
            }
        }
    }

    // Calculate duration if both start and end are valid
    if (startMinutes !== null && endMinutes !== null && endMinutes > startMinutes) {
        durationMinutes = endMinutes - startMinutes;
    }

    return { startMinutes, endMinutes, durationMinutes };
};

/**
 * Checks if two time intervals overlap.
 * @param {string} timeString1 - First time string (e.g., "HH:MM - HH:MM").
 * @param {string} timeString2 - Second time string (e.g., "HH:MM - HH:MM").
 * @returns {boolean} - True if the time intervals overlap, false otherwise.
 */
export const checkTimeOverlap = (timeString1: string, timeString2: string): boolean => {
  const time1 = parseTime(timeString1);
  const time2 = parseTime(timeString2);

  // Ensure both time strings were parsed successfully and have start/end times
  if (
    time1.startMinutes === null ||
    time1.endMinutes === null ||
    time2.startMinutes === null ||
    time2.endMinutes === null
  ) {
    // If parsing fails for either, assume no overlap to avoid blocking saves unnecessarily
    // Consider logging a warning here in a real application
    console.warn("Could not parse time strings for overlap check:", timeString1, timeString2);
    return false;
  }

  // Check for overlap:
  // Overlap occurs if time1 starts before time2 ends AND time1 ends after time2 starts.
  const overlap = time1.startMinutes < time2.endMinutes && time1.endMinutes > time2.startMinutes;

  return overlap;
};

/**
 * Formats minutes from midnight into HH:MM string.
 * @param {number} totalMinutes - Total minutes from midnight.
 * @returns {string} - Formatted time string "HH:MM".
 */
export const formatTimeFromMinutes = (totalMinutes: number | null): string => {
    if (totalMinutes === null || totalMinutes < 0) return 'N/A';
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    const formattedHours = String(hours).padStart(2, '0');
    const formattedMinutes = String(minutes).padStart(2, '0');
    return `${formattedHours}:${formattedMinutes}`;
};

/**
 * Determines the effective preference status for a given time slot based on a list of ranges.
 * 'cannot' takes precedence over 'prefer_away'. Default is 'free'.
 * @param {string} day - The day of the week (e.g., 'monday').
 * @param {number} targetMinutes - The time in minutes from midnight (start of the slot).
 * @param {Array<import('../models/Preference').default>} preferenceRanges - Array of Preference objects.
 * @param {object} STATUS_CONSTANTS - Object containing status strings (e.g., TEACHER_PREFERENCE_STATUS).
 * @returns {string} - The effective status ('free', 'prefer_away', 'cannot').
 */
export const getStatusForSlot = (day: string, targetMinutes: number | null, preferenceRanges: any[], STATUS_CONSTANTS: any): string => {
  if (targetMinutes === null || !Array.isArray(preferenceRanges)) {
    // Ensure STATUS_CONSTANTS and STATUS_CONSTANTS.FREE are accessible before returning
    if (!STATUS_CONSTANTS || !STATUS_CONSTANTS.FREE) {
        console.error("getStatusForSlot: STATUS_CONSTANTS.FREE is not defined.");
        return 'free'; // Fallback to a string literal if constants are missing
    }
    return STATUS_CONSTANTS.FREE; // Default to free if input invalid
  }

  let effectiveStatus = STATUS_CONSTANTS.FREE; // Default

  for (const range of preferenceRanges) {
    if (range.day !== day) continue; // Skip ranges for other days

    const startMinutes = timeToMinutes(range.startTime);
    // endTime might be null/undefined for "all day" or invalid
    let endMinutes = range.endTime ? timeToMinutes(range.endTime) : null;

    // Handle "all day" range (e.g., startTime='--', endTime='--' or specific format)
    // For simplicity, let's assume "all day" is represented by a range covering the whole schedule day
    // Or check for a specific marker like startTime === '--'
    const isAllDay = !range.startTime && !range.endTime; // Example: check if both are missing
    // A more robust way might be startTime='00:00', endTime='24:00'

    if (isAllDay) {
        // If it's an all-day range for the correct day
         if (range.status === STATUS_CONSTANTS.CANNOT) {
             return STATUS_CONSTANTS.CANNOT; // 'cannot' takes immediate precedence
         } else if (range.status === STATUS_CONSTANTS.PREFER_AWAY) {
             effectiveStatus = STATUS_CONSTANTS.PREFER_AWAY; // Mark as 'prefer_away' but keep checking for 'cannot'
         }
         continue; // Check next range
    }

    // Standard range check
    if (startMinutes !== null && endMinutes !== null) {
        // Check if the targetMinutes falls within the range [startMinutes, endMinutes)
        // Note: The check is usually inclusive start, exclusive end for slots.
        // E.g., 11:00-12:00 range should cover the 11:00 slot, but not the 12:00 slot.
      if (targetMinutes >= startMinutes && targetMinutes < endMinutes) {
        if (range.status === STATUS_CONSTANTS.CANNOT) {
          return STATUS_CONSTANTS.CANNOT; // 'cannot' takes immediate precedence
        } else if (range.status === STATUS_CONSTANTS.PREFER_AWAY) {
          effectiveStatus = STATUS_CONSTANTS.PREFER_AWAY; // Mark as 'prefer_away' but keep checking for 'cannot'
        }
      }
    }
  }

  return effectiveStatus;
};

export const generateUniqueCodeFromName = (name: string, existingItems: any[], codeKey: string = 'room_code', basePrefix: string = ''): string => {
    if (!name) return `${basePrefix}${uuidv4().substring(0, 6)}`; // Fallback for empty name

    let baseCode = name.toLowerCase()
        .replace(/\s+/g, '-') // Replace spaces with hyphens
        .replace(/[^a-z0-9-]/g, '') // Remove special characters except hyphen
        .substring(0, 15); // Limit length

    if (basePrefix && !baseCode.startsWith(basePrefix)) {
        baseCode = `${basePrefix}-${baseCode}`;
    } else if (!basePrefix && baseCode.length === 0) {
        baseCode = `item-${uuidv4().substring(0, 4)}`; // fallback if name results in empty string
    }
    
    baseCode = baseCode.replace(/--+/g, '-').replace(/^-+|-+$/g, ''); // Clean up multiple/trailing hyphens

    let finalCode = baseCode;
    let counter = 1;
    // Check against existing items' codes (e.g., rooms' room_code)
    // Ensure existingItems is an array and items have the codeKey property
    const safeExistingItems = Array.isArray(existingItems) ? existingItems : [];
    while (safeExistingItems.some(item => item && item[codeKey] === finalCode)) {
        finalCode = `${baseCode}-${counter}`;
        counter++;
        if (counter > 100) { // Safety break to prevent infinite loops
            finalCode = `${baseCode}-${uuidv4().substring(0, 4)}`;
            break;
        }
    }
    return finalCode;
};

/**
 * Parses a slot key "day-HH:MM" into its components.
 * @param {string} slotKey - The slot key string.
 * @returns {{day: string|null, time: string|null, slotMinutes: number|null}}
 */
export const parseSlotKey = (slotKey: string): { day: string | null; time: string | null; slotMinutes: number | null } => {
    if (!slotKey || typeof slotKey !== 'string') {
        return { day: null, time: null, slotMinutes: null };
    }
    const parts = slotKey.split('-');
    if (parts.length !== 2) {
        return { day: null, time: null, slotMinutes: null };
    }
    const day = parts[0] || null;
    const time = parts[1] || null;
    const slotMinutes = timeToMinutes(time);
    // Basic validation for day could be added if needed
    return { day, time, slotMinutes };
};

/**
 * Converts HH:MM or HH:MM:SS time string to total minutes from midnight.
 * @param {string} timeString - Time string "HH:MM" or "HH:MM:SS".
 * @returns {number|null} - Total minutes or null if invalid.
 */
export const timeToMinutes = (timeString: string | null): number | null => {
  if (!timeString || typeof timeString !== 'string') return null;

  // Regex to match HH:MM (group 2 for minutes) or HH:MM:SS (group 4 for minutes, group 6 for seconds)
  const timeRegex = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/;
  const match = timeString.match(timeRegex);

  if (match && match[1] && match[2]) {
    const hours = parseInt(match[1], 10);
    const minutes = parseInt(match[2], 10);
    // Seconds (match[3]) are ignored as per common schedule precision

    if (hours >= 0 && hours < 24 && minutes >= 0 && minutes < 60) {
      return hours * 60 + minutes;
    }
  }
  return null;
};

/**
 * Gets the start of the week (Monday) for a given date.
 * @param {Date} date - The date to get the week start for.
 * @returns {Date} - The date of the Monday of that week.
 */
export const getWeekStartDate = (date: Date): Date => {
  const d = new Date(date);
  const day = d.getDay(); // Sunday - 0, Monday - 1, ..., Saturday - 6
  const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
  return new Date(d.setDate(diff));
};

const WEEKDAY_KEYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;

/**
 * If the real-world calendar "today" falls inside the week of `currentDate` (Mon–Sun),
 * returns the active day key (e.g. 'wednesday') when that weekday is in `activeDays`; otherwise null.
 */
export const getActiveDayKeyForTodayInDisplayedWeek = (currentDate: Date, activeDays: string[]): string | null => {
  const now = new Date();
  const weekStart = getWeekStartDate(currentDate);
  const rangeStart = new Date(weekStart.getFullYear(), weekStart.getMonth(), weekStart.getDate());
  const rangeEnd = new Date(rangeStart);
  rangeEnd.setDate(rangeEnd.getDate() + 6);

  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (today < rangeStart || today > rangeEnd) {
    return null;
  }

  const delta = Math.round((today.getTime() - rangeStart.getTime()) / 86400000);
  if (delta < 0 || delta >= WEEKDAY_KEYS.length) {
    return null;
  }

  const key = WEEKDAY_KEYS[delta];
  if (key === undefined) {
    return null;
  }
  return activeDays.some((d) => d.toLowerCase() === key) ? key : null;
};

/**
 * Formats a date into a string like "1-8 Mar 2025" or "27 Apr - 4 May 2025".
 * @param {Date} startDate - The start date of the week (Monday).
 * @returns {string} - Formatted week label.
 */
export const formatWeekLabel = (startDate: Date): string => {
  const endDate = new Date(startDate);
  endDate.setDate(startDate.getDate() + 6); // Add 6 days to get to Sunday

  const startDay = startDate.getDate();
  const endDay = endDate.getDate();
  const startMonth = startDate.toLocaleString('en-US', { month: 'short' });
  const endMonth = endDate.toLocaleString('en-US', { month: 'short' });
  const startYear = startDate.getFullYear();
  const endYear = endDate.getFullYear();

  // If the week spans across two months
  if (startMonth !== endMonth) {
    return `${startDay} ${startMonth} - ${endDay} ${endMonth} ${startYear}`;
  }

  // If the week is within the same month
  return `${startDay} - ${endDay} ${startMonth} ${startYear}`;
};

/**
 * Gets the ISO week number for a given date.
 * @param {Date} date - The date.
 * @returns {number} - The ISO week number.
 */
export const getWeekNumber = (date: Date): number => {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  // Set to nearest Thursday: current date + 4 - current day number
  // Make Sunday's day number 7
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
  // Get first day of year
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  // Calculate full weeks to nearest Thursday
  const weekNo = Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
  return weekNo;
};

/**
 * Formats a day name to a short version with day number.
 * @param {string} dayName - Full day name (e.g., 'monday').
 * @param {Date} weekStartDate - The start date of the week to calculate day numbers.
 * @returns {{shortName: string, dayNumber: number, fullDate: Date}} - Object with short name, day number, and full date.
 */
export const formatDayWithNumber = (dayName: string, weekStartDate: Date): { shortName: string; dayNumber: number; fullDate: Date } => {
  const dayMapping: { [key: string]: { short: string; index: number } } = {
    'monday': { short: 'Mon', index: 0 },
    'tuesday': { short: 'Tue', index: 1 },
    'wednesday': { short: 'Wed', index: 2 },
    'thursday': { short: 'Thu', index: 3 },
    'friday': { short: 'Fri', index: 4 },
    'saturday': { short: 'Sat', index: 5 },
    'sunday': { short: 'Sun', index: 6 }
  };

  const dayInfo = dayMapping[dayName.toLowerCase()];
  if (!dayInfo) {
    return { shortName: dayName.slice(0, 3), dayNumber: 0, fullDate: weekStartDate };
  }

  const fullDate = new Date(weekStartDate);
  fullDate.setDate(weekStartDate.getDate() + dayInfo.index);
  
  return {
    shortName: dayInfo.short,
    dayNumber: fullDate.getDate(),
    fullDate: fullDate
  };
};

/**
 * Formats a date to a short readable format (e.g., "Jan 15").
 * @param {Date} date - The date to format.
 * @returns {string} - The formatted date string.
 */
export const formatDateShort = (date: Date): string => {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
    return 'Invalid Date';
  }
  
  const options: Intl.DateTimeFormatOptions = { 
    month: 'short', 
    day: 'numeric' 
  };
  
  return date.toLocaleDateString('en-US', options);
};